import { useQueries } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Session, Agent } from "@/types";

export interface AgentCost {
  agentId: string;
  agentName: string;
  totalCostUsd: number;
  sessionCount: number;
  isLoading: boolean;
  isError: boolean;
}

// Aggregiert Cost pro Agent via parallel-fetch (useQueries).
// Top-N Agents nach summe(sessions.totalCostUsd).
//
// Adversarial-Note: n+1 Fetches — bei vielen Agents (>50) wird das spürbar.
// M3 begrenzt Input via Slice; sobald >100 Agents real: Master-Endpoint
// /agents/cost-summary einführen (sum aggregiert in SQL).
export function useAgentsCostSummary(agents: Agent[] | undefined): {
  data: AgentCost[];
  isLoading: boolean;
} {
  const { session, logout } = useAuth();

  const results = useQueries({
    queries: (agents ?? []).map((agent) => ({
      queryKey: queryKeys.subList("agents", agent.id, "sessions"),
      queryFn: async () => {
        const client = createApiClient({
          getToken: () => session?.accessToken ?? null,
          onUnauthorized: logout,
        });
        const res = await client.get<{ sessions: Session[] }>(`/agents/${agent.id}/sessions`);
        return res.sessions;
      },
      enabled: !!session,
    })),
  });

  const data: AgentCost[] = (agents ?? []).map((agent, i) => {
    const r = results[i];
    const sessions = (r?.data as Session[] | undefined) ?? [];
    return {
      agentId: agent.id,
      agentName: agent.name,
      totalCostUsd: sessions.reduce((sum, s) => sum + (s.totalCostUsd ?? 0), 0),
      sessionCount: sessions.length,
      isLoading: r?.isLoading ?? false,
      isError: r?.isError ?? false,
    };
  });

  return {
    data,
    isLoading: results.some((r) => r.isLoading),
  };
}
